import { useCallback, useEffect, useState } from "react";
import { Crown, Download, RefreshCw, Save, Shuffle, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";
const primaryButton = "rounded bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-50";
const secondaryButton = "rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] hover:text-white disabled:opacity-50";

export default function VIPs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [manual, setManual] = useState({ login: "", note: "" });

  const settings = data?.settings || {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/vips");
      setData(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load VIPs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchSettings = (patch) => {
    setData((current) => ({ ...(current || {}), settings: { ...((current || {}).settings || {}), ...patch } }));
  };

  const saveSettings = async () => {
    setBusy("settings");
    try {
      const { data: payload } = await api.patch("/admin/vips/settings", settings);
      setData(payload);
      toast.success("VIP settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save VIP settings.");
    } finally {
      setBusy("");
    }
  };

  const assignManual = async () => {
    setBusy("manual");
    try {
      const { data: payload } = await api.post("/admin/vips/assign", { ...manual, source: "manual" });
      setData(payload);
      setManual({ login: "", note: "" });
      toast.success("VIP assigned.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign VIP.");
    } finally {
      setBusy("");
    }
  };

  const removeVip = async (vipId) => {
    setBusy(vipId);
    try {
      const { data: payload } = await api.delete(`/admin/vips/${vipId}`);
      setData(payload);
      toast.success("VIP removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove VIP.");
    } finally {
      setBusy("");
    }
  };

  const syncTwitch = async () => {
    setBusy("import");
    try {
      const { data: payload } = await api.post("/admin/vips/sync-twitch");
      setData(payload);
      const result = payload.result || {};
      toast.success(result.action === "synced" ? `Synced ${result.local_count || 0} Twitch VIPs.` : "Twitch VIP sync skipped.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sync Twitch VIPs.");
    } finally {
      setBusy("");
    }
  };

  const testRandom = async () => {
    setBusy("random");
    try {
      const { data: payload } = await api.post("/admin/vips/test-random");
      setData(payload);
      const action = payload.result?.action;
      toast.success(action === "assigned" ? "Random VIP assigned." : "No eligible chatter found.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not test random VIP.");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">Loading VIPs...</div>;
  }

  return (
    <div className="space-y-6" data-testid="vips-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Temporary status</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Crown className="w-6 h-6 text-[hsl(var(--primary))]" /> VIP System
          </h1>
        </div>
        <button type="button" onClick={load} className={`${secondaryButton} h-10 px-4 inline-flex items-center gap-2`}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Current VIPs</div>
            <h2 className="text-lg font-semibold mt-1">{data?.current_vips?.length || 0} of {settings.max_slots || 0} slots filled</h2>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
              This list is kept synced with Twitch when VIP read permission is available. New VIPs rotate out the oldest local/Twitch VIP when slots are full.
            </p>
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            Twitch add/remove: {data?.twitch_sync?.status || "unknown"} · Twitch list: {data?.twitch_vips?.status || "unknown"}
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          {(data?.current_vips || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No current VIPs.</div>}
          {(data?.current_vips || []).map((vip) => (
            <div key={vip.id} className="p-4 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{vip.display_name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">@{vip.login} · {vip.source}</div>
                </div>
                <button type="button" onClick={() => removeVip(vip.id)} disabled={busy === vip.id} className={`${secondaryButton} h-9 w-9 inline-flex items-center justify-center`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Assigned {vip.assigned_at}</div>
              {vip.expires_at && <div className="text-xs text-[hsl(var(--muted-foreground))]">Expires {vip.expires_at}</div>}
              <div className="mt-2 text-xs">Twitch: {vip.twitch_sync_status}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Twitch VIP list</div>
            <h2 className="text-lg font-semibold mt-1">{data?.twitch_vips?.count || 0} VIPs found on Twitch</h2>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-3xl">
              Twitch's current VIP list. If it says missing_scope, the broadcaster must re-auth with channel:read:vips. Sync mirrors Twitch VIPs into local tracking and removes stale local VIPs.
            </p>
          </div>
          <button type="button" onClick={syncTwitch} disabled={busy === "import" || data?.twitch_vips?.status !== "ok"} className={`${secondaryButton} h-10 px-4 inline-flex items-center gap-2`}>
            <Download className="w-4 h-4" /> Sync
          </button>
        </div>
        {data?.twitch_vips?.status !== "ok" ? (
          <div className="rounded border border-amber-400/45 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
            Twitch VIP lookup is {data?.twitch_vips?.status || "unknown"}{data?.twitch_vips?.required_scope ? ` (${data.twitch_vips.required_scope})` : ""}.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(data?.twitch_vips?.vips || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No Twitch VIPs returned.</div>}
            {(data?.twitch_vips?.vips || []).map((vip) => (
              <span key={vip.twitch_user_id} className="px-3 py-1.5 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm">
                @{vip.login || vip.display_name}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="surface p-5">
        <div className="label-eyebrow mb-3">Steal a VIP redeem</div>
        <h2 className="text-lg font-semibold mt-1">Channel point path</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
          Create a Twitch channel point reward titled <span className="text-white">{data?.steal_vip?.reward_title || "Steal a VIP"}</span>
          {" "}(or add that title/id under Twitch reward names on the seeded interaction).
          Redeems remove the oldest VIP on Twitch, then grant VIP to the redeemer. Hype random VIP is temporarily disabled.
        </p>
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] space-y-1">
          <div>Interaction: <span className="text-white">{data?.steal_vip?.interaction_id || "steal-a-vip"}</span> · {data?.steal_vip?.interaction_enabled ? "enabled" : "missing/disabled"}</div>
          <div>Aliases: {(data?.steal_vip?.twitch_reward_aliases || []).join(", ") || "Steal a VIP"}</div>
          <div>Redeem VIP setting: {data?.steal_vip?.allow_redeem_vip === false ? "off" : "on"} · Twitch manage: {data?.steal_vip?.twitch_manage_status || data?.twitch_sync?.status || "unknown"}</div>
          {data?.steal_vip?.last_event ? (
            <div>Last steal event: {data.steal_vip.last_event.action} · @{data.steal_vip.last_event.login || "viewer"} · {data.steal_vip.last_event.created_at}</div>
          ) : (
            <div>No steal VIP events yet.</div>
          )}
        </div>
      </section>

      <section className="surface p-5">
        <div className="label-eyebrow mb-3">Settings</div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <Toggle label="VIP system enabled" help="Master switch for local VIP assignment rules." checked={settings.enabled !== false} onChange={(enabled) => patchSettings({ enabled })} />
          <NumberField label="Max slots" help="Twitch currently allows 50 VIP slots here. Manual assigns still rotate the oldest VIP when full. Steal redeems always evict the oldest VIP when one exists." value={settings.max_slots || 50} onChange={(max_slots) => patchSettings({ max_slots })} />
          <NumberField label="Duration minutes" help="0 means VIPs last until removed or rotated." value={settings.vip_duration_minutes || 0} onChange={(vip_duration_minutes) => patchSettings({ vip_duration_minutes })} />
          <Toggle label="Twitch sync" help="Try to add/remove VIPs on Twitch when permissions are available. Steal redeems require this." checked={settings.twitch_sync_enabled !== false} onChange={(twitch_sync_enabled) => patchSettings({ twitch_sync_enabled })} />
          <Toggle label="Redeem VIP" help="Allows the Steal a VIP / Assign VIP interaction action to run for channel point redeems." checked={settings.allow_redeem_vip !== false} onChange={(allow_redeem_vip) => patchSettings({ allow_redeem_vip })} />
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 py-2 text-xs text-[hsl(var(--muted-foreground))] lg:col-span-2">
            Hype random VIP is temporarily disabled and replaced by the Steal a VIP channel point redeem.
          </div>
          <Toggle label="Bits VIP" help="A cheer or Power Up at/above the threshold grants VIP to the spender." checked={settings.allow_bits_vip === true} onChange={(allow_bits_vip) => patchSettings({ allow_bits_vip })} />
          <NumberField label="Bits threshold" help="Minimum bits in one event before VIP is granted." value={settings.bits_threshold || 5000} onChange={(bits_threshold) => patchSettings({ bits_threshold })} />
          <Toggle label="Sub VIP" help="A subscription or resub grants VIP to the subscriber." checked={settings.allow_sub_vip === true} onChange={(allow_sub_vip) => patchSettings({ allow_sub_vip })} />
          <Toggle label="Gift sub VIP" help="A gift bomb at/above the gift threshold grants VIP to the gifter." checked={settings.allow_gift_sub_vip === true} onChange={(allow_gift_sub_vip) => patchSettings({ allow_gift_sub_vip })} />
          <NumberField label="Gift threshold" help="Minimum gift count in one gift event before VIP is granted." value={settings.gift_sub_threshold || 5} onChange={(gift_sub_threshold) => patchSettings({ gift_sub_threshold })} />
          <NumberField label="Active window minutes" help="How recently someone must have chatted to be eligible for admin test-random VIP." value={settings.active_chatter_window_minutes || 15} onChange={(active_chatter_window_minutes) => patchSettings({ active_chatter_window_minutes })} />
        </div>
        <button type="button" onClick={saveSettings} disabled={busy === "settings"} className={`${primaryButton} mt-4 h-10 px-4 inline-flex items-center gap-2`}>
          <Save className="w-4 h-4" /> Save settings
        </button>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className="surface p-5">
          <div className="label-eyebrow mb-3">Manual assignment</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <TextField label="Viewer login" value={manual.login} onChange={(login) => setManual({ ...manual, login })} />
            <TextField label="Note" value={manual.note} onChange={(note) => setManual({ ...manual, note })} />
            <button type="button" onClick={assignManual} disabled={busy === "manual"} className={`${primaryButton} h-10 px-4 inline-flex items-center gap-2`}>
              <UserPlus className="w-4 h-4" /> Assign
            </button>
          </div>
          <button type="button" onClick={testRandom} disabled={busy === "random"} className={`${secondaryButton} mt-4 h-10 px-4 inline-flex items-center gap-2`}>
            <Shuffle className="w-4 h-4" /> Test random active chatter
          </button>
        </section>

        <section className="surface p-5">
          <div className="label-eyebrow mb-3">Eligible active chatters</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
            Eligible means recently active and not currently marked as mod, broadcaster, or VIP.
          </p>
          <div className="flex flex-wrap gap-2">
            {(data?.eligible_active_chatters || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No eligible active chatters right now.</div>}
            {(data?.eligible_active_chatters || []).map((viewer) => (
              <span key={viewer.twitch_user_id} className="px-3 py-1.5 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm">
                @{viewer.login}
              </span>
            ))}
          </div>
        </section>
      </div>

      <section className="surface p-5">
        <div className="label-eyebrow mb-3">VIP history</div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(data?.events || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No VIP history yet.</div>}
          {(data?.events || []).map((event) => (
            <div key={event.id} className="p-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium">{event.action} · @{event.login || "viewer"}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{event.source}</div>
              </div>
              <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{event.created_at}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Toggle({ label = "", help = "", checked, onChange } = {}) {
  return (
    <label className="flex items-start justify-between gap-3 min-h-16 px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
      <span>
        <span className="block text-sm">{label}</span>
        {help && <span className="block text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">{help}</span>}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

function NumberField({ label = "", help = "", value, onChange } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      <input className={inputClass} type="number" value={value} onChange={(event) => onChange(Number(event.target.value || 0))} />
      {help && <span className="block text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">{help}</span>}
    </label>
  );
}

function TextField({ label = "", value, onChange } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      <input className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
